import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button, Modal, Table, Form } from "react-bootstrap";
import { useAppContext } from "../../contexts/AppContext";
import RichTextForm from "../Forms/RichTextForm";

const TranslationAdmin = () => {
  const [translations, setTranslations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentTranslation, setCurrentTranslation] = useState({});
  const [search, setSearch] = useState("");
  const { backendUrl } = useAppContext();
  const translationRoute = "/translations";
  const [filteredTranslations, setFilteredTranslations] = useState([]);
  
  const [engTranslation, setEngTranslation] = useState("");
  const [tigTranslation, setTigTranslation] = useState("");
  const [amhTranslation, setAmhTranslation] = useState("");

  const fetchTranslations = useCallback(async () => {
    try {
      const response = await axios.get(`${backendUrl}${translationRoute}`);
      setTranslations(response?.data);
      setFilteredTranslations(response?.data);
    } catch (error) {
      console.error("Error fetching translations:", error);
    }
  }, [backendUrl, translationRoute]);

  useEffect(() => {
    fetchTranslations();
  }, [fetchTranslations]);

  // Debounced search logic
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (!search) {
        setFilteredTranslations(translations);
        return;
      }

      const filtered = translations.filter((item) => {
        const word = item?.word?.toLowerCase() || "";
        const eng = item?.translation?.eng?.toLowerCase() || "";
        const amh = item?.translation?.amh?.toLowerCase() || "";
        const tig = item?.translation?.tig?.toLowerCase() || "";
        return (
          word.includes(search.toLowerCase()) ||
          eng.includes(search.toLowerCase()) ||
          amh.includes(search.toLowerCase()) ||
          tig.includes(search.toLowerCase())
        );
      });

      setFilteredTranslations(filtered);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [search, translations]);

  const handleUpdate = async (id, updatedData) => {
    try {
      await axios.put(`${backendUrl}${translationRoute}/${id}`, updatedData);
      alert("Updated successfully!");
      fetchTranslations();
      setShowModal(false);
    } catch (error) {
      alert("Failed to update translation");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Do you want to delete this translation?")) {
      try {
        await axios.delete(`${backendUrl}${translationRoute}/${id}`);
        alert("Deleted successfully!");
        fetchTranslations();
      } catch (error) {
        alert("Failed to delete translation");
      }
    }
  };

  const showEditModal = (item) => {
    setCurrentTranslation(item);
    setEngTranslation(item?.translation?.eng || "");
    setTigTranslation(item?.translation?.tig || "");
    setAmhTranslation(item?.translation?.amh || "");
    setShowModal(true);
  };

  const truncateText = (html, length = 50) => {
    if (!html) return "";
    const stripped = html.replace(/<[^>]*>/g, "").trim();
    return stripped.length > length ? stripped.substring(0, length) + "..." : stripped;
  };

  return (
    <div className="bg-white rounded-3 shadow-sm p-4">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-4 gap-3">
        <h5 className="fw-bold m-0">Translation Management</h5>
        <div className="d-flex gap-2 align-items-center">
          <div className="input-group input-group-sm" style={{ width: "250px" }}>
            <span className="input-group-text bg-white border-end-0">
              <i className="bi bi-search text-muted"></i>
            </span>
            <input
              type="text"
              className="form-control border-start-0 ps-0"
              placeholder="Search keys..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button 
            variant="outline-primary" 
            size="sm" 
            onClick={fetchTranslations} 
            style={{ borderColor: "var(--primary-color)", color: "var(--primary-color)" }}
          >
            <i className="bi bi-arrow-clockwise"></i>
          </Button>
        </div>
      </div>

      <div className="table-responsive">
        <Table hover className="align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ minWidth: "150px" }}>Word/Key</th>
              <th>English</th>
              <th>Amharic</th>
              <th>Tigrinya</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTranslations.map((item) => (
              <tr key={item.id}>
                <td className="fw-bold small text-muted text-truncate" style={{ maxWidth: "200px" }}>
                  {truncateText(item.word, 40)}
                </td>
                <td className="text-truncate" style={{ maxWidth: "200px" }}>
                  {truncateText(item?.translation?.eng, 40)}
                </td>
                <td className="text-truncate" style={{ maxWidth: "200px" }}>
                  {truncateText(item?.translation?.amh, 40)}
                </td>
                <td className="text-truncate" style={{ maxWidth: "200px" }}>
                  {truncateText(item?.translation?.tig, 40)}
                </td>
                <td className="text-end">
                  <div className="d-flex justify-content-end gap-2 flex-wrap">
                    <button 
                      className="btn-action edit"
                      onClick={() => showEditModal(item)}
                      title="Edit"
                    >
                      <i className="bi bi-pencil-fill"></i>
                    </button>
                    <button 
                      className="btn-action delete"
                      onClick={() => handleDelete(item.id)}
                      title="Delete"
                    >
                      <i className="bi bi-trash3-fill"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Translation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="mb-3">
              <label className="form-label fw-bold">Key</label>
              <input type="text" className="form-control" value={currentTranslation.word} disabled />
            </div>
            <RichTextForm labelName="English Translation" value={engTranslation} onchange={setEngTranslation} />
            <RichTextForm labelName="Amharic Translation" value={amhTranslation} onchange={setAmhTranslation} />
            <RichTextForm labelName="Tigrinya Translation" value={tigTranslation} onchange={setTigTranslation} />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
          <Button variant="primary" onClick={() => handleUpdate(currentTranslation.id, { eng: engTranslation, tig: tigTranslation, amh: amhTranslation })}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
      <style>{`
        .btn-action {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          background: #f8fafc;
          cursor: pointer;
        }
        .btn-action.edit { color: var(--primary-color); }
        .btn-action.edit:hover { background: var(--primary-color); color: white; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(255,165,0,0.25); }
        .btn-action.delete { color: #ef4444; }
        .btn-action.delete:hover { background: #ef4444; color: white; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(239,68,68,0.25); }
      `}</style>
    </div>
  );
};

export default TranslationAdmin;
